import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';

import type { Waypoint } from '@/types/waypoint';
import { WaypointType } from '@/types/waypoint';
import type { BlindMetadata } from '@/types/waypoint';
import { WAYPOINT_CATEGORY_LABELS, WAYPOINT_TYPE_LABELS } from '@/components/map/map-utils';
import { MARKER_COLORS } from '@/components/map/WaypointMarker';
import { iconSvgPath } from '@/components/map/WaypointMarker';
import { isWindFavorable } from '@/utils/wind-favorability';

interface MarkerInfoCardProps {
  location: Waypoint;
  propertyId: string;
  onDismiss: () => void;
  /** When true, the wind favorability line is rendered (wind overlay is active). */
  windOverlayActive?: boolean;
  /** Current wind cardinal direction, e.g. "NW". Undefined when no weather data. */
  currentWindCardinal?: string;
  /** Current wind speed in mph. Undefined when no weather data. */
  currentWindSpeed?: number;
}

function buildMetaSummary(location: Waypoint): string | null {
  const m = location.metadata;
  if (!m) return null;
  switch (m.type) {
    case 'blind':
      return [m.blindType, m.heightFeet ? `${m.heightFeet}ft` : null, m.idealWindDirections?.length ? `Wind: ${m.idealWindDirections.join(', ')}` : null]
        .filter(Boolean)
        .join(' · ');
    case 'food_source':
      return ['plotSizeAcres' in m && m.plotSizeAcres ? `${m.plotSizeAcres} ac` : null,
              'plantedSpecies' in m ? (m.plantedSpecies?.join(', ') ?? null) : null,
              'mineralType' in m ? (m.mineralType ?? null) : null]
        .filter(Boolean)
        .join(' · ');
    case 'camera':
      return [m.cameraModel ?? null, m.cameraType ?? null]
        .filter(Boolean)
        .join(' · ');
    case 'water_source':
      return m.waterType ?? null;
    case 'game_trail':
      return m.directionOfTravel ?? null;
    case 'bedding_area':
      return [m.beddingType ?? null, m.coverDensity ?? null].filter(Boolean).join(' · ');
    // mineral_site is now a subtype of food_source; handled in 'food_source' case above
    default:
      return null;
  }
}

export function MarkerInfoCard({ location, propertyId, onDismiss, windOverlayActive, currentWindCardinal, currentWindSpeed }: MarkerInfoCardProps) {
  const navigate = useNavigate();
  const cardRef = useRef<HTMLDivElement>(null);

  const color = MARKER_COLORS[location.type];
  const categoryLabel = WAYPOINT_CATEGORY_LABELS[location.type];
  const typeLabel = WAYPOINT_TYPE_LABELS[location.type];
  const metaSummary = buildMetaSummary(location);
  const isAccessPoint = location.type === WaypointType.AccessPoint;

  // Wind favorability line — only for blinds when wind overlay is active
  const isBlind = location.type === WaypointType.Blind;
  const blindMeta = isBlind ? (location.metadata as BlindMetadata | undefined) : undefined;
  const idealWindDirs = blindMeta?.idealWindDirections ?? [];
  const windFavorability = windOverlayActive && isBlind
    ? isWindFavorable(currentWindCardinal, idealWindDirs)
    : null;

  // Dismiss on tap outside the card
  useEffect(() => {
    function handlePointerDown(e: PointerEvent) {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
        onDismiss();
      }
    }
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [onDismiss]);

  function handleViewDetails() {
    void navigate(`/land/properties/${propertyId}/waypoints/${location.id}`);
  }

  function handleEdit() {
    void navigate(`/land/properties/${propertyId}/waypoints/${location.id}?edit=true`);
  }

  function handleNavigate() {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${location.latitude},${location.longitude}`;
    window.open(url, '_blank');
  }

  return (
    <div
      ref={cardRef}
      style={{ zIndex: 1100 }}
      className="absolute bottom-0 left-0 right-0 animate-slide-up"
      role="dialog"
      aria-label={`Waypoint details: ${location.name}`}
    >
      <div data-testid="info-card-body" className="bg-bark-800 rounded-t-2xl border-t-[0.5px] border-bark-600 shadow-card-md px-4 pt-3 pb-6 max-h-[40vh] overflow-y-auto">
        {/* Drag handle */}
        <div className="w-10 h-1 rounded-full bg-bark-600 mx-auto mb-3" aria-hidden="true" />

        {/* Header row */}
        <div className="flex items-start justify-between gap-3 mb-3">
          {/* Left: icon + text stack */}
          <div className="flex items-center gap-3 min-w-0">
            {/* Type color circle with glyph */}
            <div
              style={{ background: color, flexShrink: 0 }}
              className="w-9 h-9 rounded-full border-2 border-white/20 flex items-center justify-center shadow-md"
              aria-hidden="true"
              // SAFETY: dangerouslySetInnerHTML is used here with sanitized SVG from a controlled
              // internal constant (iconSvgPath). The SVG contains only path/line/circle elements
              // with white stroke — no user-supplied content, no scripts.
              dangerouslySetInnerHTML={{ __html: iconSvgPath(location.type, 18) }}
            />
            {/* Text stack */}
            <div className="min-w-0">
              {/* Field-label category line */}
              <p className="field-label leading-tight mb-0.5">
                {categoryLabel}
              </p>
              {/* DM Serif Display name */}
              <p className="font-serif text-[20px] leading-tight text-bark-50 truncate">
                {location.name}
              </p>
            </div>
          </div>
        </div>

        {/* Type label (secondary, below name) */}
        <p className="text-body-xs text-bark-400 mb-2 -mt-1 ml-12">{typeLabel}</p>

        {/* Metadata summary */}
        {metaSummary && (
          <p className="text-body-sm text-bark-300 mb-3 leading-relaxed">
            {metaSummary}
          </p>
        )}

        {/* Wind favorability line — blind + wind overlay active */}
        {windFavorability !== null && windFavorability !== 'unknown' && (
          <p
            data-testid="wind-favorability-line"
            data-favorability={windFavorability}
            className="text-body-sm mb-3 leading-relaxed font-medium"
            style={{ color: windFavorability === 'favorable' ? 'var(--color-favorable)' : 'var(--color-accent)' }}
          >
            {windFavorability === 'favorable' ? '\u2713' : '\u2717'}{' '}
            {windFavorability === 'favorable' ? 'Favorable' : 'Unfavorable'}
            {currentWindCardinal && currentWindSpeed != null
              ? ` \u2014 ${currentWindCardinal} ${Math.round(currentWindSpeed)} mph`
              : ''}
          </p>
        )}

        {location.notes && (
          <p className="text-body-sm text-bark-400 mb-3 leading-relaxed line-clamp-2">
            {location.notes}
          </p>
        )}

        {/* Action bar */}
        <div className="flex items-center gap-2 mt-1">
          {/* View Details — secondary button */}
          <button
            type="button"
            onClick={handleViewDetails}
            className="flex-1 py-2.5 rounded-lg bg-bark-700 border border-bark-600 text-bark-100 text-body-sm font-medium hover:bg-bark-600 transition-colors text-center"
          >
            View Details
          </button>

          {/* Edit — ghost button */}
          <button
            type="button"
            onClick={handleEdit}
            className="px-4 py-2.5 text-bark-300 hover:text-bark-100 text-body-sm font-medium transition-colors"
          >
            Edit
          </button>

          {/* Navigate — ember icon button, AccessPoint only */}
          {isAccessPoint && (
            <button
              type="button"
              onClick={handleNavigate}
              aria-label="Navigate to location"
              className="w-11 h-11 rounded-lg bg-ember-500 shadow-ember flex items-center justify-center hover:opacity-90 active:opacity-80 transition-opacity shrink-0"
            >
              <ArrowUpRight size={22} className="text-white" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
